import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/shared/PageHeader';
import { TemplateBuilder } from './components/TemplateBuilder';
import { useDocumentTemplates } from '../../core/hooks/useDocumentTemplates';
import { useAuth } from '../../core/contexts/AuthContext';
import type { DocumentTemplateType } from '../../core/models/Document';
import { FileText, ArrowLeft } from 'lucide-react';

export const TemplateCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { organizationId } = useAuth();
  const { createTemplate } = useDocumentTemplates(organizationId);
  const [template, setTemplate] = useState<Partial<DocumentTemplateType>>({
    name: '',
    description: '',
    templatePath: '',
    dynamicFields: [],
    isActive: true,
    organizationId: organizationId || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!template.name || !template.organizationId) {
      return;
    }

    if (!template.dynamicFields || template.dynamicFields.length === 0) {
      alert('Please add at least one field to the template');
      return;
    }

    if (!template.content || template.content.trim() === '') {
      alert('HTML content is required. Please enter the template HTML.');
      return;
    }

    setIsSubmitting(true);
    try {
      await createTemplate({
        name: template.name,
        description: template.description,
        templatePath: template.templatePath || '',
        content: template.content || '', // Store HTML content
        dynamicFields: template.dynamicFields,
        isActive: template.isActive ?? true,
        organizationId: template.organizationId,
      } as Omit<DocumentTemplateType, 'id' | 'createdAt' | 'updatedAt'>);
      navigate('/documents/templates');
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Failed to create template. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Create Document Template"
        description="Build a reusable document template with dynamic fields"
        icon={FileText}
        actions={
          <Button variant="outline" onClick={() => navigate('/documents/templates')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Templates
          </Button>
        }
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <TemplateBuilder template={template} onChange={setTemplate} />

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/documents/templates')}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            Create Template
          </Button>
        </div>
      </form>
    </div>
  );
};
